import { ReactElement, cloneElement, useRef, useState, useLayoutEffect } from 'react';
import LoadingSpinner from './LoadingSpinner';

interface ChartWrapperProps {
  children: ReactElement;
  height?: number;
  className?: string;
  isModalOpen?: boolean;
  style?: React.CSSProperties;
}

/**
 * Wrapper for Nivo charts that ensures proper sizing using ResizeObserver.
 */
export function ChartWrapper({
  children,
  height = 400,
  className = '',
  isModalOpen = true,
  style = {},
}: ChartWrapperProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);

  useLayoutEffect(() => {
    if (!containerRef.current || !isModalOpen) {
      setDimensions(null);
      return;
    }

    const container = containerRef.current;

    const rect = container.getBoundingClientRect();
    setDimensions({ width: rect.width, height });

    const resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        const { width } = entry.contentRect;
        setDimensions({ width, height });
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isModalOpen, height]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        height,
        width: '100%',
        ...style,
      }}
    >
      {dimensions ? (
        cloneElement(children, {
          width: dimensions.width,
          height: dimensions.height,
          ...children.props,
        })
      ) : (
        <div
          className="flex items-center justify-center text-[var(--anl-text-muted)]"
          style={{ height }}
        >
          <LoadingSpinner size="lg" />
        </div>
      )}
    </div>
  );
}
